import React, { useEffect, useRef, useState } from 'react';
import { GlassWater, Utensils, Music, Wine, Church, PartyPopper } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';
import { useIntersectionObserver } from '../utils/animations';

// Componente SVG personalizado para anillos de boda
const WeddingRings = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24"
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    {/* Anillo izquierdo */}
    <circle cx="9" cy="10" r="3" />
    {/* Anillo derecho */}
    <circle cx="15" cy="14" r="3" />
    {/* Conectando los anillos */}
    <path d="M11.3 9.8L12.7 14.2" />
  </svg>
);

interface TimelineEvent {
  id: string;
  time: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  position: 'left' | 'right';
  quote?: string;
  titleFont?: FontFamily;
  eventTitleFont?: FontFamily;
  eventTextFont?: FontFamily;
}

// Tipos de fuentes disponibles
export type FontFamily = 'italiana' | 'parisienne' | 'dancing' | 'sans' | 'garamond' | 'lora' | 'baskerville';

interface ItineraryProps {
  titleFont?: FontFamily;
  eventTitleFont?: FontFamily;
  eventTextFont?: FontFamily;
}

const timelineEvents: TimelineEvent[] = [
  {
    id: 'ceremony',
    time: '2:00 PM',
    title: 'WE DO',
    titleFont: 'baskerville',
    description: 'Ceremonia Religiosa',
    icon: <Church className="w-10 h-10 text-sage font-baskerville" />,
    position: 'left',
   },
  {
    id: 'cocktail',
    time: '4:00 PM',
    title: 'WE DRINK',
    titleFont: 'baskerville',
    eventTitleFont: 'baskerville',
    eventTextFont: 'baskerville',
    description: 'Recepción y cóctel',
    icon: <Wine className="w-10 h-10 text-sage font-baskerville" />,
    position: 'right',
  },
  {
    id: 'dinner',
    time: '6:00 PM',
    title: 'WE EAT',
    titleFont: 'baskerville',
    eventTitleFont: 'baskerville',
    eventTextFont: 'baskerville',
    description: 'Cena',
    icon: <Utensils className="w-10 h-10 text-sage font-baskerville" />,
    position: 'left',
  },
  {
    id: 'party',
    time: '8:30 PM',
    title: 'WE DANCE',
    titleFont: 'baskerville',
    eventTitleFont: 'baskerville',
    eventTextFont: 'baskerville',
    description: 'A bailar y celebrar',
    icon: <PartyPopper className="w-10 h-10 text-sage font-baskerville" />,
    position: 'right',
  }
];

const Itinerary: React.FC<ItineraryProps> = ({ 
  titleFont = 'parisienne', 
  eventTitleFont = 'baskerville',
  eventTextFont = 'baskerville'
}) => {
  const [visibleEvents, setVisibleEvents] = useState<number[]>([]);
  const [lineHeight, setLineHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const eventRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lineRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const finalPointRef = useRef<HTMLDivElement>(null);
  const firstPointRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  // Actualizar la función para calcular la altura de la línea
  useEffect(() => {
    const updateLineHeight = () => {
      if (containerRef.current && lineRef.current && eventRefs.current.length > 0 && finalPointRef.current) {
        // Obtenemos el contenedor y la posición de la línea
        const containerRect = containerRef.current.getBoundingClientRect();
        
        // Calculamos la altura total del contenedor para extender la línea completa
        const firstEvent = eventRefs.current[0];
        const finalPoint = finalPointRef.current;
        
        if (firstEvent && finalPoint) {
          const firstRect = firstEvent.getBoundingClientRect();
          const finalPointRect = finalPoint.getBoundingClientRect();
          
          // La línea debe extenderse desde antes del primer evento hasta el punto final
          const startY = firstRect.top - containerRect.top - 120; // Extendemos 120px hacia arriba
          const endY = finalPointRect.top - containerRect.top + -120; // Valor fijo
          
          const newHeight = endY - startY;
          
          // Aplicamos la nueva altura y ajustamos la posición
          lineRef.current.style.height = `${newHeight}px`;
          lineRef.current.style.top = `${startY}px`;
        }
      }
    };

    updateLineHeight();
    
    // Añadimos listener para actualizar la altura cuando cambia el tamaño de la ventana
    window.addEventListener('resize', updateLineHeight);
    
    // Actualizar la línea cuando se haga scroll
    const handleScroll = () => {
      requestAnimationFrame(updateLineHeight);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', updateLineHeight);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Observer para detectar cuando los eventos están visibles
  useEffect(() => {
    const observerOptions = {
      threshold: 0.3
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Iteramos manualmente para encontrar el índice del elemento
        let index = -1;
        for (let i = 0; i < eventRefs.current.length; i++) {
          if (eventRefs.current[i] === entry.target) {
            index = i;
            break;
          }
        }
        
        if (index !== -1) {
          if (entry.isIntersecting && !visibleEvents.includes(index)) {
            setVisibleEvents((prev) => [...prev, index]);
          }
        }
      });
    }, observerOptions);

    eventRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      eventRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [visibleEvents]);

  // useEffect para vincular el titleRef con el hook
  useEffect(() => {
    if (titleRef.current) {
      const element = titleRef.current;
      // El ref se asigna al elemento del DOM en el JSX más adelante
    }
  }, []);

  return (
    <section 
      id="itinerary" 
      className="py-20 px-4 relative overflow-hidden"
    >
      <div className="max-w-4xl mx-auto relative z-10" ref={containerRef}>
        {/* Título siempre visible */}
        <div
          ref={titleRef}
          className="text-center mb-16"
        >
          <h2 className={`text-4xl md:text-5xl font-${titleFont} text-forest mb-6`}>
            Itinerario
          </h2>
          <div className="w-24 h-0.5 bg-sage/30 mx-auto"></div>
        </div>
        
        <div className="relative">
          {/* Línea de tiempo - extendida para llegar a los bordes */}
          <div 
            ref={lineRef} 
            className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-sage/50"
            style={{ 
              height: `${lineHeight}px`,
              top: 0
            }}
          ></div>
          
          {/* Contenedor de eventos */}
          <div className="space-y-16 relative pt-16 pb-16">
            {timelineEvents.map((event, index) => (
              <div
                key={index}
                ref={(el) => (eventRefs.current[index] = el)}
                className={`timeline-event relative flex flex-col md:flex-row items-center md:items-start transition-all duration-1000 transform ${
                  visibleEvents.includes(index) 
                    ? 'opacity-100' 
                    : 'opacity-0'
                } ${
                  event.position === 'left' 
                    ? 'md:flex-row' 
                    : 'md:flex-row-reverse'
                }`}
                style={{ 
                  transitionDelay: `${index * 200}ms`,
                  transform: visibleEvents.includes(index) 
                    ? 'translateY(0)' 
                    : 'translateY(20px)'
                }}
              >
                {/* Punto en la línea del timeline - el primero será más grande */}
                <div 
                  ref={index === 0 ? firstPointRef : null}
                  className={`${index === 0 ? 'w-6 h-6' : 'w-4 h-4'} bg-sage rounded-full border-2 border-cream z-10 mb-4 md:mb-0 md:absolute md:left-1/2 md:transform md:-translate-x-1/2`}
                ></div>
                
                <div className={`w-full md:w-5/12 flex flex-col items-center ${
                  event.position === 'left' 
                    ? 'md:items-end md:mr-auto md:pr-8' 
                    : 'md:items-start md:ml-auto md:pl-8'
                }`}>
                  <div className={`text-center glass-card p-6 rounded-lg shadow-sm ${
                    event.position === 'left' 
                      ? 'md:text-right' 
                      : 'md:text-left'
                    } md:order-1 flex flex-col items-center w-full`}
                  >
                    <p className={`text-forest/70 mb-1 font-${event.eventTextFont || eventTextFont}`}>{event.time}</p>
                    <h3 className={`font-${event.eventTitleFont || eventTitleFont} text-3xl mb-1 text-forest`}>{event.title}</h3>
                    <p className={`text-forest/80 mb-2 font-${event.eventTextFont || eventTextFont}`}>{event.description}</p>
                    {event.quote && (
                      <p className={`text-sm italic text-forest/60 mt-2 font-${event.eventTextFont || eventTextFont}`}>
                        "{event.quote}"
                      </p>
                    )}
                    
                    <div className="mt-4 p-3 bg-cream/80 rounded-full shadow-sm z-10">
                      {event.icon}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Punto final de la línea de tiempo */}
            <div 
              className={`text-center pt-12 relative transition-all duration-1000 transform ${
                visibleEvents.length === timelineEvents.length 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${timelineEvents.length * 200 + 200}ms` }}
            >
              <div 
                ref={finalPointRef}
                className="absolute left-1/2 transform -translate-x-1/2 -top-6 w-6 h-6 bg-sage rounded-full border-2 border-cream z-10"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Itinerary;
